import { useState } from 'react';
import type { Trade } from '../../types';
import { Button } from '../ui/Button';
import { useAuth } from '../../context/AuthContext';
import { useTradeAction, useRateTrade } from '../../hooks/useTrades';

const statusConfig: Record<
  Trade['status'],
  { label: string; bg: string; text: string; dot: string }
> = {
  PENDING:   { label: 'Pendiente',  bg: 'bg-amber-100',  text: 'text-amber-700',  dot: 'bg-amber-400'  },
  ACCEPTED:  { label: 'Aceptado',   bg: 'bg-blue-100',   text: 'text-blue-700',   dot: 'bg-blue-500'   },
  COMPLETED: { label: 'Completado', bg: 'bg-green-100',  text: 'text-green-700',  dot: 'bg-green-500'  },
  REJECTED:  { label: 'Rechazado',  bg: 'bg-red-100',    text: 'text-red-700',    dot: 'bg-red-500'    },
  CANCELLED: { label: 'Cancelado',  bg: 'bg-gray-100',   text: 'text-gray-400',   dot: 'bg-gray-300'   },
  EXPIRED:   { label: 'Expirado',   bg: 'bg-gray-100',   text: 'text-gray-400',   dot: 'bg-gray-300'   },
};

function timeLeft(expiresAt: string): string {
  const ms = new Date(expiresAt).getTime() - Date.now();
  if (ms <= 0) return 'Vencido';
  const h = Math.floor(ms / 3_600_000);
  if (h < 24) return `${h}h restantes`;
  return `${Math.floor(h / 24)}d ${h % 24}h`;
}

// ── Star rating component ────────────────────────────────────────────────────
function StarRating({ value, onChange }: { value: number; onChange: (v: number) => void }) {
  const [hover, setHover] = useState(0);
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          onClick={() => onChange(star)}
          onMouseEnter={() => setHover(star)}
          onMouseLeave={() => setHover(0)}
          className="text-2xl transition-transform hover:scale-110"
        >
          {star <= (hover || value) ? '⭐' : '☆'}
        </button>
      ))}
    </div>
  );
}

// ── Rate modal ───────────────────────────────────────────────────────────────
function RateModal({
  tradeId,
  otherUsername,
  onClose,
}: {
  tradeId: string;
  otherUsername: string;
  onClose: () => void;
}) {
  const rateTrade = useRateTrade();
  const [score, setScore] = useState(0);
  const [comment, setComment] = useState('');
  const [error, setError] = useState('');

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (score === 0) { setError('Selecciona una calificación'); return; }
    setError('');
    rateTrade.mutate(
      { tradeId, score, comment: comment || undefined },
      { onSuccess: onClose, onError: () => setError('Error al calificar. Intenta de nuevo.') }
    );
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-end sm:items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl p-5 w-full max-w-sm shadow-xl">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-gray-800">Calificar a {otherUsername}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 font-bold text-lg">✕</button>
        </div>
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div>
            <p className="text-sm text-gray-500 font-semibold mb-2">¿Cómo fue el intercambio?</p>
            <StarRating value={score} onChange={setScore} />
          </div>
          <div>
            <label className="block text-xs font-bold text-gray-400 uppercase tracking-wide mb-1.5">
              Comentario (opcional)
            </label>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Todo bien, entrega rápida..."
              rows={3}
              maxLength={300}
              className="input-field resize-none text-sm"
            />
          </div>
          {error && <p className="text-sm text-red-600 font-semibold">{error}</p>}
          <div className="flex gap-2">
            <Button type="submit" loading={rateTrade.isPending} disabled={score === 0}>
              Enviar calificación
            </Button>
            <Button variant="secondary" onClick={onClose}>Omitir</Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Main card ────────────────────────────────────────────────────────────────
interface TradeCardProps {
  trade: Trade;
}

export function TradeCard({ trade }: TradeCardProps) {
  const { user } = useAuth();
  const tradeAction = useTradeAction();
  const [showRating, setShowRating] = useState(false);
  const cfg = statusConfig[trade.status];

  const isProposer = user?.userId === trade.proposerId;
  const isTarget   = user?.userId === trade.targetId;
  const isPending  = trade.status === 'PENDING';
  const isAccepted = trade.status === 'ACCEPTED';
  const isCompleted = trade.status === 'COMPLETED';

  // Contact info for the other party
  const otherEmail    = isProposer ? trade.targetEmail    : trade.proposerEmail;
  const otherUsername = isProposer ? trade.targetUsername : trade.proposerUsername;
  const otherCiudad   = isProposer ? trade.targetCiudad   : trade.proposerCiudad;
  const otherWhatsapp = isProposer ? trade.targetWhatsapp : trade.proposerWhatsapp;
  const otherNota     = isProposer ? trade.targetNota     : trade.proposerNota;

  // Confirmation status
  const iConfirmed    = isProposer ? trade.proposerConfirmed : trade.targetConfirmed;
  const otherConfirmed = isProposer ? trade.targetConfirmed  : trade.proposerConfirmed;

  // Rating status
  const iRated = isProposer ? !!trade.proposerRating : !!trade.targetRating;

  function handleAction(action: 'accept' | 'reject' | 'cancel' | 'confirm') {
    tradeAction.mutate({ tradeId: trade.tradeId, action });
  }

  return (
    <>
      <div className="sticker-card p-4 flex flex-col gap-3">
        {/* Header */}
        <div className="flex items-center justify-between">
          <span className={`inline-flex items-center gap-1.5 text-xs font-bold px-2.5 py-1 rounded-full ${cfg.bg} ${cfg.text}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
            {cfg.label}
          </span>
          <span className="text-xs text-gray-400 font-semibold">
            {new Date(trade.createdAt).toLocaleDateString('es', { dateStyle: 'medium' })}
          </span>
        </div>

        {/* Role */}
        <div className="text-xs font-bold text-gray-500">
          {isProposer ? '📤 Tú propones' : isTarget ? '📥 Te proponen' : '👥 Intercambio'}
        </div>

        {/* Stickers */}
        <div className="flex items-center gap-2">
          <StickerChip label="Ofrece" ids={trade.offeredStickers} color="#8B5CF6" />
          <span className="text-lg text-gray-300 flex-shrink-0">⇄</span>
          <StickerChip label="Pide" ids={trade.requestedStickers} color="#F59E0B" />
        </div>

        {/* Expiry (pending only) */}
        {isPending && trade.expiresAt && (
          <div className="text-xs text-amber-600 font-semibold">
            ⏱ {timeLeft(trade.expiresAt)}
          </div>
        )}

        {/* Contact info (accepted) */}
        {isAccepted && (otherEmail || otherUsername) && (
          <div className="bg-blue-50 border border-blue-100 rounded-xl px-3 py-2.5 flex flex-col gap-1">
            <p className="text-xs font-bold text-blue-700 mb-0.5">📬 Datos del otro coleccionista</p>
            {otherUsername && <p className="text-sm font-semibold text-gray-700">👤 {otherUsername}</p>}
            {otherEmail    && <p className="text-sm font-semibold text-gray-700">✉️ {otherEmail}</p>}
            {otherCiudad   && <p className="text-sm font-semibold text-gray-700">📍 {otherCiudad}</p>}
            {otherWhatsapp && (
              <a
                href={`https://wa.me/${otherWhatsapp.replace(/\D/g, '')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm font-semibold text-green-600 hover:underline"
              >
                📱 {otherWhatsapp}
              </a>
            )}
            {otherNota && (
              <p className="text-xs text-gray-500 font-semibold mt-0.5 italic">"{otherNota}"</p>
            )}
            <p className="text-xs text-blue-500 font-semibold mt-1">
              Coordinen la entrega y luego confirmen aquí
            </p>
          </div>
        )}

        {/* Confirmation status (accepted) */}
        {isAccepted && (
          <div className="flex gap-3 text-xs font-semibold">
            <span className={iConfirmed ? 'text-green-600' : 'text-gray-400'}>
              {iConfirmed ? '✓' : '○'} Tú {iConfirmed ? 'confirmaste' : 'pendiente'}
            </span>
            <span className={otherConfirmed ? 'text-green-600' : 'text-gray-400'}>
              {otherConfirmed ? '✓' : '○'} {otherUsername ?? 'El otro'} {otherConfirmed ? 'confirmó' : 'pendiente'}
            </span>
          </div>
        )}

        {/* Action error */}
        {tradeAction.isError && (
          <p className="text-xs text-red-600 font-semibold">
            Error al procesar la acción. Intenta de nuevo.
          </p>
        )}

        {/* Actions */}
        <div className="flex gap-2 pt-1 border-t border-gray-50">
          {/* PENDING actions */}
          {isPending && isTarget && (
            <>
              <Button size="sm" variant="primary" onClick={() => handleAction('accept')} loading={tradeAction.isPending} className="flex-1">
                ✓ Aceptar
              </Button>
              <Button size="sm" variant="danger" onClick={() => handleAction('reject')} loading={tradeAction.isPending}>
                ✕ Rechazar
              </Button>
            </>
          )}
          {isPending && isProposer && (
            <Button size="sm" variant="secondary" onClick={() => handleAction('cancel')} loading={tradeAction.isPending} className="flex-1">
              Cancelar propuesta
            </Button>
          )}

          {/* ACCEPTED actions */}
          {isAccepted && !iConfirmed && (
            <Button size="sm" variant="primary" onClick={() => handleAction('confirm')} loading={tradeAction.isPending} className="flex-1">
              ✓ Confirmar entrega recibida
            </Button>
          )}
          {isAccepted && iConfirmed && !otherConfirmed && (
            <p className="text-xs text-gray-400 font-semibold self-center">
              Esperando confirmación de {otherUsername ?? 'el otro usuario'}...
            </p>
          )}

          {/* COMPLETED: rate if not yet rated */}
          {isCompleted && !iRated && (isProposer || isTarget) && (
            <Button size="sm" variant="primary" onClick={() => setShowRating(true)} className="flex-1">
              ⭐ Calificar a {otherUsername ?? 'el otro usuario'}
            </Button>
          )}
          {isCompleted && iRated && (
            <p className="text-xs text-green-600 font-semibold self-center">✓ Ya calificaste este intercambio</p>
          )}
        </div>
      </div>

      {/* Rating modal */}
      {showRating && (
        <RateModal
          tradeId={trade.tradeId}
          otherUsername={otherUsername ?? 'el otro usuario'}
          onClose={() => setShowRating(false)}
        />
      )}
    </>
  );
}

function StickerChip({ label, ids, color }: { label: string; ids: string[]; color: string }) {
  return (
    <div className="flex-1 flex flex-col items-center gap-1">
      <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wide">{label}</span>
      <div
        className="w-full py-2 rounded-xl flex flex-col items-center justify-center gap-0.5 font-bold text-sm"
        style={{ backgroundColor: color + '22', color }}
      >
        {ids.map((id) => <span key={id}>#{id}</span>)}
      </div>
    </div>
  );
}
